/**
 * CC_07 Block 4 — Pop-Up 1 „Spalte hinzufügen/bearbeiten" (Mockup v3). Replaces
 * the old window.prompt pair with a typed dialog: pick a type, then configure it.
 * The column menu (Typ-Config) reuses this dialog in edit mode. Generic actions
 * (Sortieren/Filtern/Breite) are NOT here — they live on the toolbar / header.
 *
 * Per-type config carried in `config`:
 *   text     → { default, multiline }        (Standardwert + „Mehrzeilig", F1)
 *   select   → { options:[{value,label,color}], default }
 *   relation → { mode:"row", cardinality, self, targetListId, backColumn }  (F2)
 *   number / url → Feinkonfiguration zurückgestellt (CC_07 Abschneide → CC_08)
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";

import { SelectConfigEditor, type SelectOpt } from "./SelectConfigEditor";

import "./ryfe.scss";

import type { ListColumn, ListColumnType } from "../api";

// Mockup v3 A — nine pickable types. „Whiteboard" is a synthetic picker entry
// that maps to a board-relation column (type='relation', config.mode='board').
type PickType = ListColumnType | "board";
const TYPES: { type: PickType; label: string; icon: string }[] = [
  { type: "text", label: "Text", icon: "T" },
  { type: "number", label: "Zahl", icon: "#" },
  { type: "checkbox", label: "Checkbox", icon: "✓" },
  { type: "date", label: "Datum", icon: "▦" },
  { type: "select", label: "Auswahl", icon: "◈" },
  { type: "url", label: "URL", icon: "↗" },
  // CC_11 Block 6 (Migration 020) — the consolidated widening: the render half
  // shipped in CC_09 (P22) / CC_10 (D-56); the picker offers them now that the
  // backend allowlist + DB CHECK accept them.
  { type: "url_text", label: "URL + Text", icon: "↗T" },
  { type: "color", label: "Farbe", icon: "◐" },
  { type: "webhooks", label: "Webhooks", icon: "⚡" },
  { type: "uuid", label: "UUID", icon: "ID" },
  { type: "relation", label: "Relation", icon: "⇄" },
  // AP-6 (P7.3) — umbenannt von „Whiteboard": diese Spalte legt je Zeile eine
  // Datei ODER einen Ordner an (create_kind steuert was). Der Picker-Wert bleibt
  // „board" (gespeichert als relation, mode board).
  { type: "board", label: "Auto-Create-Files-With-Rows", icon: "▣" },
];
const ON_DELETE = [
  { v: "nothing", label: "Nichts" },
  { v: "archive", label: "Archivieren" },
  { v: "delete", label: "Löschen" },
] as const;

// Mockup v3 B/C — Kardinalität 1:1 · 1:n · n:1 · n:n (Default 1:n).
const CARDINALITIES = ["1:1", "1:n", "n:1", "n:n"] as const;

export interface ColumnDraft {
  name: string;
  type: ListColumnType;
  config: Record<string, unknown>;
}

export const ColumnDialog = ({
  wsId,
  currentFileId,
  initial,
  onClose,
  onSubmit,
}: {
  wsId: string;
  currentFileId: string;
  initial?: ListColumn | null;
  onClose: () => void;
  onSubmit: (draft: ColumnDraft) => void | Promise<void>;
}) => {
  const cfg0 = (initial?.config ?? {}) as Record<string, unknown>;
  const [name, setName] = useState(initial?.name ?? "");
  // A board-relation column shows up in the picker as the „Whiteboard" type.
  const [type, setType] = useState<PickType>(
    initial?.type === "relation" && cfg0.mode === "board"
      ? "board"
      : initial?.type ?? "text",
  );

  // text
  const [textDefault, setTextDefault] = useState(String(cfg0.default ?? ""));
  const [multiline, setMultiline] = useState(Boolean(cfg0.multiline));
  // select
  const [options, setOptions] = useState<SelectOpt[]>(
    Array.isArray(cfg0.options) ? (cfg0.options as SelectOpt[]) : [],
  );
  const [selectDefault, setSelectDefault] = useState(
    String(cfg0.default ?? ""),
  );
  const [multiple, setMultiple] = useState(Boolean(cfg0.multiple));
  // checkbox / date (Mockup v3 B)
  const [checkboxDefault, setCheckboxDefault] = useState(Boolean(cfg0.default));
  const [dateFormat, setDateFormat] = useState(
    typeof cfg0.dateFormat === "string" ? cfg0.dateFormat : "TT.MM.JJJJ",
  );
  const [dateTime, setDateTime] = useState(Boolean(cfg0.time));
  // relation (F2) + Auto-Board
  const [cardinality, setCardinality] = useState(
    typeof cfg0.cardinality === "string" ? cfg0.cardinality : "1:n",
  );
  const [self, setSelf] = useState(Boolean(cfg0.self));
  const [targetListId, setTargetListId] = useState(
    typeof cfg0.targetListId === "string" ? cfg0.targetListId : "",
  );
  const [backColumn, setBackColumn] = useState(Boolean(cfg0.backColumn));
  const [onDelete, setOnDelete] = useState(
    typeof cfg0.on_row_delete === "string" ? cfg0.on_row_delete : "nothing",
  );
  const [boardNameFrom, setBoardNameFrom] = useState(
    cfg0.title_column ? "title" : "listname",
  );
  // Welle B — the auto-relation may create a Whiteboard, a Notiz (md) or a
  // Liste per row (config.create_kind, default 'board').
  const [createKind, setCreateKind] = useState(
    typeof cfg0.create_kind === "string" ? cfg0.create_kind : "board",
  );
  const [lists, setLists] = useState<{ id: string; name: string }[]>([]);
  // number (CC_08) — Separatoren + Währung
  const [decimalSep, setDecimalSep] = useState(
    typeof cfg0.decimalSep === "string" ? cfg0.decimalSep : ".",
  );
  const [thousandSep, setThousandSep] = useState(
    typeof cfg0.thousandSep === "string" ? cfg0.thousandSep : "",
  );
  const [currency, setCurrency] = useState(
    typeof cfg0.currency === "string" ? cfg0.currency : "",
  );
  // url (CC_08) — Link/Button + Label
  const [urlDisplay, setUrlDisplay] = useState<"link" | "button">(
    cfg0.display === "button" ? "button" : "link",
  );
  const [urlLabel, setUrlLabel] = useState(
    typeof cfg0.label === "string" ? cfg0.label : "",
  );
  // CC_11 Block 6 (D-56) — webhooks column: one URL per line (allowlist is
  // enforced server-side, D-45) + an optional action filter.
  const [hookUrls, setHookUrls] = useState(
    Array.isArray(cfg0.hooks)
      ? (cfg0.hooks as { url?: string }[])
          .map((h) => h.url || "")
          .filter(Boolean)
          .join("\n")
      : "",
  );
  const [hookAction, setHookAction] = useState(
    Array.isArray(cfg0.hooks) &&
      (cfg0.hooks as { actions?: string[] }[])[0]?.actions?.length === 1
      ? String((cfg0.hooks as { actions?: string[] }[])[0].actions![0])
      : "all",
  );

  // Target picker (D-42, Fix CC_11-Revision): ALLE lesbaren Listen des
  // Workspace, ordnerunabhängig — vorher wurden nur die Listen im Root-Ordner
  // angeboten, Listen in Unterordnern waren nicht auswählbar.
  useEffect(() => {
    if (type !== "relation" || self) {
      return;
    }
    let cancelled = false;
    ryfeApi
      .listWorkspaceLists(wsId)
      .then((res) => {
        if (cancelled) {
          return;
        }
        setLists(
          res.lists
            .filter((f) => f.id !== currentFileId)
            .map((f) => ({ id: f.id, name: f.name })),
        );
      })
      .catch(() => setLists([]));
    return () => {
      cancelled = true;
    };
  }, [type, self, wsId, currentFileId]);

  const buildConfig = (): Record<string, unknown> => {
    switch (type) {
      case "text":
        return { default: textDefault || undefined, multiline };
      case "select":
        return {
          options: options.filter((o) => o.value.trim() !== ""),
          default: selectDefault || undefined,
          multiple,
        };
      case "checkbox":
        return { default: checkboxDefault };
      case "date":
        return { dateFormat, time: dateTime };
      case "relation":
        return {
          mode: "row",
          cardinality,
          self,
          // Self-relation targets this list; otherwise the chosen list.
          targetListId: self ? currentFileId : targetListId || undefined,
          backColumn,
          on_row_delete: onDelete,
        };
      case "board":
        // Mockup v3 B — Auto-Board: a board-relation created per new row.
        // Welle B — create_kind chooses what the row creates (board|md|list).
        return {
          mode: "board",
          auto_create: true,
          create_kind: createKind,
          title_column: boardNameFrom === "title" ? true : undefined,
          on_row_delete: onDelete,
        };
      case "number":
        // CC_08 — Separatoren + Währung (aus config).
        return {
          decimalSep,
          thousandSep,
          currency: currency || undefined,
        };
      case "url":
        // CC_08 — Link/Button-Umschaltung + Label.
        return { display: urlDisplay, label: urlLabel || undefined };
      case "webhooks":
        // CC_11 Block 6 (D-56) — hooks from the textarea (one URL per line);
        // the https/make.com allowlist is authoritative on the server.
        return {
          hooks: hookUrls
            .split("\n")
            .map((u) => u.trim())
            .filter(Boolean)
            .map((url) => ({
              url,
              ...(hookAction !== "all" ? { actions: [hookAction] } : {}),
            })),
        };
      default:
        return {};
    }
  };

  const submit = async () => {
    if (!name.trim()) {
      return;
    }
    // „Whiteboard" is stored as a board-relation column.
    const emitType: ListColumnType = type === "board" ? "relation" : type;
    await onSubmit({
      name: name.trim(),
      type: emitType,
      config: buildConfig(),
    });
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">
          {initial ? "Spalte bearbeiten" : "Spalte hinzufügen"}
        </h2>

        <label className="ryfe-field-label">
          Name
          <input
            className="ryfe-field"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="ryfe-field-label">
          Typ
          <select
            className="ryfe-field"
            value={type}
            onChange={(e) => setType(e.target.value as PickType)}
          >
            {TYPES.map((t) => (
              <option key={t.type} value={t.type}>
                {t.icon} {t.label}
              </option>
            ))}
          </select>
        </label>

        {/* ---- per-type config ---- */}
        {type === "checkbox" && (
          <div className="ryfe-coldlg__cfg">
            <label className="ryfe-check">
              <input
                type="checkbox"
                checked={checkboxDefault}
                onChange={(e) => setCheckboxDefault(e.target.checked)}
              />
              Standard: angehakt
            </label>
          </div>
        )}

        {type === "date" && (
          <div className="ryfe-coldlg__cfg">
            <label className="ryfe-field-label">
              Format
              <select
                className="ryfe-field"
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value)}
              >
                <option value="TT.MM.JJJJ">TT.MM.JJJJ</option>
                <option value="JJJJ-MM-TT">JJJJ-MM-TT</option>
              </select>
            </label>
            <label className="ryfe-check">
              <input
                type="checkbox"
                checked={dateTime}
                onChange={(e) => setDateTime(e.target.checked)}
              />
              Uhrzeit einschließen
            </label>
          </div>
        )}

        {type === "uuid" && (
          <p className="ryfe-muted ryfe-coldlg__deferred">
            Auto-generiert (read-only) · v4 — keine weitere Konfiguration.
          </p>
        )}

        {type === "board" && (
          <div className="ryfe-coldlg__cfg">
            <span className="ryfe-elmenu__head">AUTO-DATEI</span>
            <p className="ryfe-muted" style={{ margin: "0.25rem 0" }}>
              Legt je neuer Zeile eine Datei an und verknüpft sie.
            </p>
            <label className="ryfe-field-label">
              Erstellt
              <select
                className="ryfe-field"
                value={createKind}
                onChange={(e) => setCreateKind(e.target.value)}
                aria-label="Auto-Datei-Typ"
              >
                <option value="board">Whiteboard</option>
                <option value="md">Notiz (Markdown)</option>
                <option value="list">Liste</option>
                <option value="folder">Ordner</option>
              </select>
            </label>
            <label className="ryfe-field-label">
              Datei-Name aus
              <select
                className="ryfe-field"
                value={boardNameFrom}
                onChange={(e) => setBoardNameFrom(e.target.value)}
              >
                <option value="title">Titel-Spalte</option>
                <option value="listname">Listenname + Nr.</option>
              </select>
            </label>
            <span className="ryfe-elmenu__head">BEIM LÖSCHEN DER ZEILE</span>
            <div className="ryfe-elmenu__seg">
              {ON_DELETE.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  className={
                    onDelete === o.v
                      ? "ryfe-elmenu__size is-active"
                      : "ryfe-elmenu__size"
                  }
                  onClick={() => setOnDelete(o.v)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {type === "text" && (
          <div className="ryfe-coldlg__cfg">
            <label className="ryfe-field-label">
              Standardwert (Platzhalter)
              <input
                className="ryfe-field"
                value={textDefault}
                onChange={(e) => setTextDefault(e.target.value)}
              />
            </label>
            <label className="ryfe-check">
              <input
                type="checkbox"
                checked={multiline}
                onChange={(e) => setMultiline(e.target.checked)}
              />
              Mehrzeilig / Langtext
            </label>
          </div>
        )}

        {type === "select" && (
          <SelectConfigEditor
            options={options}
            setOptions={setOptions}
            selectDefault={selectDefault}
            setSelectDefault={setSelectDefault}
            multiple={multiple}
            setMultiple={setMultiple}
          />
        )}

        {type === "relation" && (
          <div className="ryfe-coldlg__cfg">
            <span className="ryfe-elmenu__head">KARDINALITÄT</span>
            <div className="ryfe-elmenu__seg">
              {CARDINALITIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={
                    cardinality === c
                      ? "ryfe-elmenu__size is-active"
                      : "ryfe-elmenu__size"
                  }
                  onClick={() => setCardinality(c)}
                >
                  {c}
                </button>
              ))}
            </div>
            <label className="ryfe-check">
              <input
                type="checkbox"
                checked={self}
                onChange={(e) => setSelf(e.target.checked)}
              />
              Self-Relation (Ziel = diese Liste)
            </label>
            {!self && (
              <label className="ryfe-field-label" data-testid="target-list">
                Ziel-Liste
                <select
                  className="ryfe-field"
                  value={targetListId}
                  onChange={(e) => setTargetListId(e.target.value)}
                >
                  <option value="">— wählen —</option>
                  {lists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="ryfe-check">
              <input
                type="checkbox"
                checked={backColumn}
                onChange={(e) => setBackColumn(e.target.checked)}
              />
              Spalte in Ziel-Liste erstellen (Rück-Spalte)
            </label>
            <span className="ryfe-elmenu__head">
              BEIM LÖSCHEN EINER VERKNÜPFTEN ZEILE
            </span>
            <div className="ryfe-elmenu__seg">
              {ON_DELETE.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  className={
                    onDelete === o.v
                      ? "ryfe-elmenu__size is-active"
                      : "ryfe-elmenu__size"
                  }
                  onClick={() => setOnDelete(o.v)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {type === "number" && (
          <div className="ryfe-coldlg__cfg">
            <label className="ryfe-field-label">
              Dezimal-Trennzeichen
              <select
                className="ryfe-field"
                value={decimalSep}
                onChange={(e) => setDecimalSep(e.target.value)}
              >
                <option value=".">Punkt (.)</option>
                <option value=",">Komma (,)</option>
              </select>
            </label>
            <label className="ryfe-field-label">
              Tausender-Trennzeichen
              <select
                className="ryfe-field"
                value={thousandSep}
                onChange={(e) => setThousandSep(e.target.value)}
              >
                <option value="">keins</option>
                <option value=".">Punkt (.)</option>
                <option value=",">Komma (,)</option>
                <option value=" ">Leerzeichen</option>
              </select>
            </label>
            <label className="ryfe-field-label">
              Währung
              <select
                className="ryfe-field"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="">keine</option>
                <option value="€">€ Euro</option>
                <option value="$">$ Dollar</option>
                <option value="£">£ Pfund</option>
                <option value="CHF">CHF</option>
              </select>
            </label>
          </div>
        )}

        {type === "url" && (
          <div className="ryfe-coldlg__cfg">
            <span className="ryfe-elmenu__head">ANZEIGE</span>
            <div className="ryfe-elmenu__seg">
              {(["link", "button"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={
                    urlDisplay === d
                      ? "ryfe-elmenu__size is-active"
                      : "ryfe-elmenu__size"
                  }
                  onClick={() => setUrlDisplay(d)}
                >
                  {d === "link" ? "Link" : "Button"}
                </button>
              ))}
            </div>
            <label className="ryfe-field-label">
              Label (optional)
              <input
                className="ryfe-field"
                value={urlLabel}
                placeholder="statt der URL anzeigen"
                onChange={(e) => setUrlLabel(e.target.value)}
              />
            </label>
          </div>
        )}

        {/* CC_11 Block 6 (D-56) — Webhooks: URLs (Allowlist serverseitig) +
            Action-Filter. Der Zellwert bleibt leer (config-only Spalte). */}
        {type === "webhooks" && (
          <div className="ryfe-coldlg__cfg">
            <label className="ryfe-field-label">
              Webhook-URLs (eine je Zeile, nur https://…make.com)
              <textarea
                className="ryfe-field"
                rows={3}
                value={hookUrls}
                placeholder="https://hook.eu2.make.com/…"
                onChange={(e) => setHookUrls(e.target.value)}
              />
            </label>
            <label className="ryfe-field-label">
              Auslösen bei
              <select
                className="ryfe-select"
                value={hookAction}
                onChange={(e) => setHookAction(e.target.value)}
              >
                <option value="all">Allen Zeilen-Ereignissen</option>
                <option value="create">Nur Anlegen</option>
                <option value="update">Nur Ändern</option>
                <option value="delete">Nur Löschen</option>
              </select>
            </label>
          </div>
        )}

        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            disabled={!name.trim()}
            onClick={() => void submit()}
          >
            {initial ? "Speichern" : "Anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
};
